import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import CustomerInfoCard from 'components/CustomerInfoCard'
import DetailPanel from 'components/DetailPanel'
import { FeedbackTableLinkState } from 'components/FeedbackTable'
import GrantRewardModal from 'components/GrantRewardModal'
import RecentActivitiesTable from 'components/RecentActivitiesTable'
import ResolveWithReplyModal from 'components/ResolveWithReplyModal'
import SendMessageModal from 'components/SendMessageModal'
import Timeline from 'components/Timeline'
import { buildTranslate } from 'locales'
import React from 'react'
import type { StaticContext } from 'react-router'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import CaseDetailsCard from './CaseDetailsCard'
import CustomerResponseCard from './CustomerResponseCard'
import RatingDetailHeader from './RatingDetailHeader'
import { formatDate } from './ratingFormatters'
import { FEEDBACK_DETAILS_PATH, useRatingDetail } from './useRatingDetail'

const t = buildTranslate('order_ratings.rating_detail')

type Props = RouteComponentProps<
  { ratingId: string },
  StaticContext,
  // Router state is present only when arrived via the table's View link; a deep
  // link or a refresh has none, which is why the query below always runs.
  FeedbackTableLinkState
>

// Detail page for a rating with NO RecoveryAI conversation (non-RAI,
// operator-dismissed, or unresolved). Deliberately the same page family as
// scenes/RecoveryAI/CaseDetail — same header and single-scroll card stack,
// built from the same promoted components (Card, Timeline, FieldGrid,
// CustomerInfoCard, RecentActivitiesTable) — with the customer's comment
// standing in for the conversation transcript.
//
// This component is pure composition: useRatingDetail owns every query,
// mutation, permission read, the modal-open state, and the derived display
// values, returning a discriminated view. The scene renders the guards and,
// when ready, wires the presentational header + cards + the three modals.
const RatingDetail = ({ match, location, history }: Props) => {
  const [css] = useStyletron()
  const view = useRatingDetail({
    ratingId: match.params.ratingId,
    stateRow: location.state?.row,
    navigate: path => history.push(path),
  })

  if (view.status === 'redirect') {
    return <Redirect to={view.to} />
  }

  if (view.status === 'loading') {
    return (
      <div className="container py-xl">
        <Text.BodyText3 color="grey70">{t('loading')}</Text.BodyText3>
      </div>
    )
  }

  if (view.status === 'error') {
    return (
      <div className="container py-xl">
        <Text.BodyText3 color="grey70">{t('error')}</Text.BodyText3>
        <div className={css({ marginTop: '12px' })}>
          <Button kind="secondary" onClick={view.onRetry}>
            {t('retry')}
          </Button>
        </div>
      </div>
    )
  }

  if (view.status === 'notFound') {
    return (
      <div className="container py-xl">
        <Text.BodyText3 color="grey70">{t('not_found')}</Text.BodyText3>
      </div>
    )
  }

  const { modals } = view

  return (
    <>
      <RatingDetailHeader
        backTo={FEEDBACK_DETAILS_PATH}
        badgeStatus={view.badgeStatus}
        title={view.title}
        isTitleLoading={view.isTitleLoading}
        subtitle={view.subtitle}
        pager={view.pager}
        showActions={view.showActions}
        actions={view.actions}
      />

      {/* Grey body area (Option B ≈ the design system's lightest grey);
          full-bleed like the white header band, with the divider between them.
          minHeight fills the viewport so short records don't leave a white gap. */}
      <div
        className={css({
          backgroundColor: merchantTheme.colors.grey05,
          minHeight: '100vh',
        })}
      >
        <div className="container py-xl">
          {/* Single vertical scroll (redesign phase 3): every section a sibling
              card, in the mockup's order. No tabs. */}
          <CustomerResponseCard comment={view.comment} />

          <CaseDetailsCard
            items={view.detailItems}
            order={view.order}
            showOrderError={view.showOrderError}
            showOrderUnavailable={view.showOrderUnavailable}
            onRetryOrder={view.onRetryOrder}
          />

          <DetailPanel>
            <Text.Header4>{t('timeline.heading')}</Text.Header4>
            <div className={css({ marginTop: '12px' })}>
              <Timeline
                items={view.timelineItems}
                ariaLabel={t('timeline.heading')}
              />
            </div>
          </DetailPanel>

          {/* Both panels are the case detail's, promoted to src/components.
              Each mounts only once its own query has resolved, so an unknown
              customer (or a failed lookup) leaves the page looking exactly as
              it did before these sections existed. */}
          {view.customerInfo && view.profileUrl && (
            <CustomerInfoCard
              customer={view.customerInfo}
              profileTo={view.profileUrl}
              variant="order-rating"
            />
          )}

          <RecentActivitiesTable
            key={match.params.ratingId}
            rows={view.recentRatingRows}
            formatDate={formatDate}
            variant="order-rating"
            dateTooltip={t('recent_activities.date_tooltip')}
          />

          {/* The promoted presentational modals, wired to the hook's mutations.
              Each is mounted only for a viewer who can actually perform the
              action AND only while the rating is open — a successful resolve
              refetches the rating resolved, which unmounts them. */}
          {modals.canManageFeedback &&
            modals.canManageRewardIssuance &&
            !modals.isRatingResolved && (
              <GrantRewardModal
                isOpen={modals.isGrantModalOpen}
                customerName={modals.customerName}
                rewards={modals.availableRewards}
                isFetchingRewards={modals.isFetchingRewards}
                hasRewardsError={modals.isRewardsError}
                showPointsOption={modals.showPointsOption}
                operatorEmail={modals.operatorEmail}
                onConfirm={modals.onGrantConfirm}
                isSubmitting={modals.isResolving}
                isError={modals.isResolveError}
                onResetError={modals.resetResolve}
                errorMessage={t('resolve.error')}
                onClose={modals.onCloseGrant}
              />
            )}

          {/* The mark-as-resolved confirmation, sharing the SAME resolve mutation
              as the grant modal. Only one of the two is ever open, and both clear
              the error on close. */}
          {modals.canManageFeedback && !modals.isRatingResolved && (
            <ResolveWithReplyModal
              isOpen={modals.isResolveModalOpen}
              operatorEmail={modals.operatorEmail}
              onConfirm={modals.onResolveConfirm}
              isSubmitting={modals.isResolving}
              isError={modals.isResolveError}
              onResetError={modals.resetResolve}
              errorMessage={t('resolve.error')}
              onClose={modals.onCloseResolve}
            />
          )}

          {/* Send-only: emails the customer WITHOUT resolving/mutating the
              rating. Mounted only for a viewer who can send and only while the
              rating is open. customerEmailState renders the right recipient
              affordance and only blocks Send when there's genuinely no recipient. */}
          {modals.canManageFeedback && !modals.isRatingResolved && (
            <SendMessageModal
              isOpen={modals.isSendMessageModalOpen}
              customerEmail={modals.customerEmailState}
              customerName={modals.customerName}
              operatorEmail={modals.operatorEmail}
              onSend={modals.onSendMessage}
              isSubmitting={modals.isSendingMessage}
              isError={modals.isSendMessageError}
              onResetError={modals.resetSendMessage}
              errorMessage={modals.sendMessageErrorMessage}
              onClose={modals.onCloseSendMessage}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default RatingDetail
